/*
 * Copyright (c) 2024-2026 GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

import {Injectable} from '@angular/core';
import {EsciMessageService} from '../shared/esci-message.service';
import {FieldRule, ValidationModel} from '../model/validation.model';
import {CommonHelperService} from './common-helper.service';

@Injectable({
  providedIn: 'root'
})
export class ValidationService {

  constructor(protected messageService: EsciMessageService) { }

    public collectFieldValidationErrors(rowList: Array<ValidationModel>, showValue: boolean):
        Map<ValidationModel, Map<string, string>> {
        const rowErrors = new Map<ValidationModel, Map<string, string>>();
        for (const row of rowList || []) {
            const fieldErrors = new Map<string, string>();
            for (const fieldRule of row.fieldRules || []) {
                const fieldMessage = this.validateField(fieldRule, row, showValue);
                if (fieldMessage) {
                    fieldErrors.set(fieldRule.fieldName, fieldMessage);
                }
            }
            if (fieldErrors.size > 0) {
                rowErrors.set(row, fieldErrors);
            }
        }
        return rowErrors;
    }

    validateFields(
        rowList: Array<ValidationModel>,
        precomputedErrors?: Map<ValidationModel, Map<string, string>>
    ) {
        const errorMessages = new Map<string, string[]>();
        const rowErrors = precomputedErrors ?? this.collectFieldValidationErrors(rowList, true);
        for (const [row, fieldErrors] of rowErrors.entries()) {
            const forMessage = this.getForMessage(row);
            for (const [fieldName, fieldMessage] of fieldErrors.entries()) {
                const message = `${forMessage}${fieldName} ${fieldMessage}`;
                let idList = errorMessages.get(message);

                if (!idList) {
                    idList = [];
                    errorMessages.set(message, idList);
                }
                idList.push(row['id'] || ' ');
            }
        }
        if (errorMessages.size === 0) {
            return false;
        }
        for (const [errorMessage, idList] of errorMessages) {
            this.messageService.processError(`Validation failure - ${errorMessage} Error(s) on row with IDs ` +
                `[${idList.sort(ValidationService.compareRowIds)}] Please correct the field and try again.`);
        }
        return true;
    }

    // Array.prototype.sort() with no comparator sorts elements as strings, so numeric row IDs like
    // [10, 23, 100] end up ordered as [10, 100, 23]. Compare numerically when both sides are valid
    // numbers, falling back to a string compare for the ' ' placeholder pushed for rows without an id.
    private static compareRowIds(a: any, b: any): number {
        const numA = Number(a);
        const numB = Number(b);
        if (!Number.isNaN(numA) && !Number.isNaN(numB) && String(a).trim() !== '' && String(b).trim() !== '') {
            return numA - numB;
        }
        return String(a).localeCompare(String(b));
    }

    public validateField(fieldRule: FieldRule, row: ValidationModel, showValue: boolean): string {
        const rawValues = this.flattenValues(this.getDeepField(fieldRule, row));
        const values = rawValues.map(v => this.normalizeValueForRule(fieldRule, v));

        if (fieldRule.required && values.length === 0 && !fieldRule.allowsBlankValue) {
            if (this.isCollectionFieldWithoutRows(fieldRule, row)) {
                return null;
            }
            return 'field is required.';
        }

        for (let i = 0; i < values.length; i++) {
            const singleValue = values[i];
            // A real DB-backed reference object (positive id) is a legitimate selection even when the
            // display value it resolves to (via allowedValueKey) is blank - e.g. RingTerminal's Plating
            // id=41 resolves to plating: "". Such a selection already satisfies "required" via object
            // identity, so it must not be re-rejected by the blank-string checks below.
            // This is gated on fieldRule.allowsBlankIdBackedReference (computed per-field from that
            // field's OWN allowedValues) so it never masks a genuine required/pattern failure on some
            // other reference column whose data has no legitimate blank-labeled option.
            const isSelectedReference = !!fieldRule.allowsBlankIdBackedReference &&
                                        this.isSelectedReferenceValue(rawValues[i]);
            if (fieldRule.required && !isSelectedReference && !this.isPresentValue(singleValue, fieldRule)) {
                return 'field is required.';
            }
            if (singleValue === undefined || singleValue === null) {
                // We know the value is not required, so no further validation needed for this element
                continue;
            }
            const valString = showValue ? ` (value: '${singleValue}')` : '';
            if (!fieldRule.type || fieldRule.type === 'string') {
                if (fieldRule.maxLength && typeof singleValue === 'string' && singleValue.length > fieldRule.maxLength) {
                    return `field exceeds the maximum length of ${fieldRule.maxLength}${valString}.`;
                }
            }
            if (fieldRule.type === 'number') {
                const numericValue = Number(singleValue);
                if (Number.isNaN(numericValue)) {
                    return `field is not a valid number${valString}.`;
                }
                if (fieldRule.maxLength && numericValue > fieldRule.maxLength) {
                    return `field must be less than or equal to ${fieldRule.maxLength}${valString}.`;
                }
                if (fieldRule.minLength && numericValue < fieldRule.minLength) {
                    return `field must be greater than ${fieldRule.minLength}${valString}.`;
                }
            }
            const pattern = fieldRule.pattern;
            const regex = pattern instanceof RegExp ? pattern : (pattern ? new RegExp(pattern) : null);
            if (regex && !isSelectedReference && !regex.test(String(singleValue))) {
                return `field does not match the pattern ${fieldRule.patternName || fieldRule.pattern}${valString}.`;
            }
        }

        return null;
    }

    checkDuplicate(r: ValidationModel, row: ValidationModel) {
        // If we are comparing the same row, not a duplicate
        if (r === row) {
            return false;
        }
        // Normalize ordering to make duplicate checks independent of caller parameter order.
        // This keeps one-direction reporting while allowing single-row validation to compare all rows.
        const hasComparableIds = r['id'] != null && row['id'] != null;
        const primary = hasComparableIds && r['id'] > row['id'] ? row : r;
        const secondary = hasComparableIds && r['id'] > row['id'] ? r : row;
        // if no field rules, or none of them are marked unique, return false
        if (!primary.fieldRules || !primary.fieldRules.some(fieldRule => fieldRule.uniqueFieldSetName)) {
            return false;
        }

        // check the field rules and if both rows have the same values in each field marked unique, this is a duplicate
        const isFieldSame = (row1, row2, fieldRule) => {
            const field1 = this.getDeepField(fieldRule, row1);
            const field2 = this.getDeepField(fieldRule, row2);
            if (field1 === field2) {
                return true;
            }
            // part_number_with_smart_search and other numeric rules may be number from API but string from the grid editor
            if (fieldRule.type === 'number') {
                if (field1 === null || field1 === undefined || field1 === '' ||
                    field2 === null || field2 === undefined || field2 === '') {
                    return false;
                }
                const n1 = Number(field1);
                const n2 = Number(field2);
                if (!Number.isNaN(n1) && !Number.isNaN(n2)) {
                    return n1 === n2;
                }
            }
            if (fieldRule.type === 'string') {
                return (field1?.toUpperCase() || '') === (field2?.toUpperCase() || '');
            }
            return false;
        };
        // Map uniqueFieldSetName to array of FieldRule
        const rFieldRulesMap = new Map<string, FieldRule[]>();
        primary.fieldRules.forEach(rule => {
            if (rule.uniqueFieldSetName) {
                if (!rFieldRulesMap.has(rule.uniqueFieldSetName)) {
                    rFieldRulesMap.set(rule.uniqueFieldSetName, []);
                }
                rFieldRulesMap.get(rule.uniqueFieldSetName).push(rule);
            }
        });

        // Compare all field rules in each set
        for (const rFieldRules of rFieldRulesMap.values()) {
            if (rFieldRules.every(rowRule => isFieldSame(primary, secondary, rowRule))) {
                const columns = rFieldRules.map(o=>o.fieldName).join(', ');
                const values = rFieldRules.map(o => '\'' + CommonHelperService.getDeepValue(primary, o.fieldName) + '\'')
                                          .join(', ');
                const colAndVals = `This is a duplicate entry: columns [${columns}] with values [${values}].`;
                const forMessage = this.getForMessage(secondary);
                this.messageService.processError(`${colAndVals} ${forMessage} Please correct the row and try again.`);
                return true;
            }
        }
        return false;
    }

    private getDeepField(fieldRule: FieldRule, r: ValidationModel) {
        return CommonHelperService.getDeepValue(r, fieldRule.fieldName);
    }

    private isCollectionFieldWithoutRows(fieldRule: FieldRule, row: ValidationModel): boolean {
        const fieldName = fieldRule?.fieldName;
        if (!fieldName || !fieldName.includes('[]')) {
            return false;
        }
        const arrayPath = fieldName.split('[]')[0];
        const collection = CommonHelperService.getDeepValue(row, arrayPath);
        if (collection === null || collection === undefined) {
            return true;
        }
        return Array.isArray(collection) && collection.length === 0;
    }

    private flattenValues(value: any): any[] {
        if (Array.isArray(value)) {
            return value.flatMap(v => this.flattenValues(v));
        }
        if (value === undefined || value === null) {
            return [];
        }
        return [value];
    }

    private normalizeValueForRule(fieldRule: FieldRule, value: any): any {
        if (Array.isArray(value)) {
            return value.map(v => this.normalizeValueForRule(fieldRule, v));
        }
        if (value && typeof value === 'object' && fieldRule.allowedValueKey) {
            if (fieldRule.allowedValueKey in value) {
                return value[fieldRule.allowedValueKey];
            }
        }
        // Backward-compatible fallback for object-backed numeric fields where
        // metadata does not include allowedValueKey (e.g. connectorService.additionalPartType).
        if (value && typeof value === 'object' && fieldRule.type === 'number') {
            if ('id' in value) {
                return value.id;
            }
            if ('code' in value) {
                return value.code;
            }
        }
        return value;
    }

    private isSelectedReferenceValue(rawValue: any): boolean {
        return !!(rawValue && (typeof rawValue === 'object') && (typeof rawValue.id === 'number') && rawValue.id > 0);
    }

    private isPresentValue(value: any, fieldRule: FieldRule): boolean {
        if (fieldRule.allowsBlankValue && (value === null || value === undefined)) {
            return true;
        }
        if (value === 0) {
            return true;
        }
        if (fieldRule.allowsBlankValue && typeof value === 'string' && value.trim().length === 0) {
            return true;
        }
        if (typeof value === 'string') {
            if (!fieldRule.type || fieldRule.type === 'string') {
                // Required string emptiness is validated via pattern to support enums that intentionally allow blank labels.
                return true;
            }
            return value.trim().length > 0;
        }
        return !!value;
    }

    private getForMessage(row: ValidationModel) {
        // iterate through every field rule in the row and concatenate the fields that have forErrorMessage value
        const msg = (row?.fieldRules || [])
            .filter(fieldRule => fieldRule.forErrorMessage)
            .map(fieldRule => {
                const value = this.getDeepField(fieldRule, row);
                return `${fieldRule.forErrorMessage}: ${value}`;
            })
            .join(', ');
        return msg ? `Row with ${msg} - ` : '';
    }
}
